// orbit.ts — orbit/pan/zoom camera for the splat views. The camera circles a
// target in openscan's Z-up world: azimuth around +Z, elevation above the
// marker plane, distance in metres. Pointer events cover mouse and touch:
// one pointer orbits (right button / shift pans), two pointers pinch-zoom and
// pan by their midpoint, the wheel zooms. A short, still press is reported as
// a tap so the Viewer's measure tool can pick on it. A host can claim a
// one-pointer primary-button press first (onGrab, e.g. a box handle): the
// returned handler then gets that pointer's moves instead of the orbit, and
// the press is never a tap.

import { mat4LookAt, mat4Perspective, vec3Add, vec3Cross, vec3Normalize, vec3Scale } from './math';
import type { Mat4, Vec3 } from './math';

export interface OrbitState {
  target: Vec3;
  azimuth: number; // rad, around +Z from +X
  elevation: number; // rad, above the XY plane
  distance: number; // m
  fovy: number; // rad
}

export const MIN_ELEVATION = -Math.PI / 2 + 0.01;
export const MAX_ELEVATION = Math.PI / 2 - 0.01;

// eyePosition places the camera on its sphere around the target.
export function eyePosition(s: OrbitState): Vec3 {
  const ce = Math.cos(s.elevation);
  return vec3Add(s.target, [
    s.distance * ce * Math.cos(s.azimuth),
    s.distance * ce * Math.sin(s.azimuth),
    s.distance * Math.sin(s.elevation),
  ]);
}

export function orbitView(s: OrbitState): Mat4 {
  return mat4LookAt(eyePosition(s), s.target, [0, 0, 1]);
}

export function orbitProj(s: OrbitState, aspect: number): Mat4 {
  const near = Math.max(0.001, s.distance * 0.01);
  const far = Math.max(10, s.distance * 100);
  return mat4Perspective(s.fovy, aspect, near, far);
}

// panOffset converts a screen-space drag (px, +x right, +y down) into a world
// translation of the target in the camera's image plane.
export function panOffset(s: OrbitState, dxPx: number, dyPx: number, viewportHeightPx: number): Vec3 {
  const worldPerPx = (2 * s.distance * Math.tan(s.fovy / 2)) / Math.max(1, viewportHeightPx);
  const fwd = vec3Normalize([
    -Math.cos(s.elevation) * Math.cos(s.azimuth),
    -Math.cos(s.elevation) * Math.sin(s.azimuth),
    -Math.sin(s.elevation),
  ]);
  const right = vec3Normalize(vec3Cross(fwd, [0, 0, 1]));
  const up = vec3Cross(right, fwd);
  return vec3Add(vec3Scale(right, -dxPx * worldPerPx), vec3Scale(up, dyPx * worldPerPx));
}

// fitState frames an axis-aligned box from a 3/4 view.
export function fitState(min: Vec3, max: Vec3, fovy = (50 * Math.PI) / 180): OrbitState {
  const target: Vec3 = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
  const radius = Math.max(0.02, 0.5 * Math.hypot(max[0] - min[0], max[1] - min[1], max[2] - min[2]));
  return { target, azimuth: -Math.PI / 4, elevation: Math.PI / 5, distance: (radius / Math.sin(fovy / 2)) * 1.1, fovy };
}

export interface TapEvent {
  x: number; // CSS px relative to the element
  y: number;
}

// GrabHandler receives a claimed pointer's moves (CSS px) and its release.
// A second finger landing mid-grab ends it (end) and starts a pinch.
export interface GrabHandler {
  move: (x: number, y: number) => void;
  end: () => void;
}

const TAP_MAX_MOVE_PX = 6;
const TAP_MAX_MS = 350;

export class OrbitControls {
  state: OrbitState;
  onChange: (() => void) | null = null;
  onTap: ((t: TapEvent) => void) | null = null;
  onGrab: ((t: TapEvent) => GrabHandler | null) | null = null;
  private grab: { id: number; h: GrabHandler } | null = null;
  private el: HTMLElement | null = null;
  private pointers = new Map<number, { x: number; y: number; button: number; shift: boolean }>();
  private pinchDist = 0;
  private pinchMid: [number, number] = [0, 0];
  private down: { x: number; y: number; t: number; moved: boolean } | null = null;

  constructor(initial: OrbitState) {
    this.state = { ...initial };
  }

  set(s: Partial<OrbitState>): void {
    this.state = { ...this.state, ...s };
    this.state.elevation = Math.min(MAX_ELEVATION, Math.max(MIN_ELEVATION, this.state.elevation));
    this.state.distance = Math.max(0.005, this.state.distance);
    this.onChange?.();
  }

  view(): Mat4 {
    return orbitView(this.state);
  }

  proj(aspect: number): Mat4 {
    return orbitProj(this.state, aspect);
  }

  attach(el: HTMLElement): void {
    this.detach();
    this.el = el;
    el.style.touchAction = 'none';
    el.addEventListener('pointerdown', this.handleDown);
    el.addEventListener('pointermove', this.handleMove);
    el.addEventListener('pointerup', this.handleUp);
    el.addEventListener('pointercancel', this.handleUp);
    el.addEventListener('wheel', this.handleWheel, { passive: false });
    el.addEventListener('contextmenu', this.preventMenu);
  }

  detach(): void {
    const el = this.el;
    if (!el) return;
    el.removeEventListener('pointerdown', this.handleDown);
    el.removeEventListener('pointermove', this.handleMove);
    el.removeEventListener('pointerup', this.handleUp);
    el.removeEventListener('pointercancel', this.handleUp);
    el.removeEventListener('wheel', this.handleWheel);
    el.removeEventListener('contextmenu', this.preventMenu);
    this.el = null;
    this.pointers.clear();
    this.endGrab();
  }

  private preventMenu = (e: Event): void => e.preventDefault();

  private local(e: PointerEvent | WheelEvent): [number, number] {
    const r = this.el?.getBoundingClientRect();
    return r ? [e.clientX - r.left, e.clientY - r.top] : [e.clientX, e.clientY];
  }

  private twoPointerGeometry(): { dist: number; mid: [number, number] } {
    const [a, b] = [...this.pointers.values()];
    return { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: [(a.x + b.x) / 2, (a.y + b.y) / 2] };
  }

  private endGrab(): void {
    const g = this.grab;
    this.grab = null;
    g?.h.end();
  }

  private handleDown = (e: PointerEvent): void => {
    const [x, y] = this.local(e);
    this.el?.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, { x, y, button: e.button, shift: e.shiftKey });
    if (this.pointers.size === 1 && e.button === 0 && !e.shiftKey && this.onGrab) {
      const h = this.onGrab({ x, y });
      if (h) {
        this.grab = { id: e.pointerId, h };
        this.down = null;
        return;
      }
    }
    if (this.pointers.size > 1) this.endGrab();
    if (this.pointers.size === 1) this.down = { x, y, t: performance.now(), moved: false };
    else this.down = null; // a second finger means this is not a tap
    if (this.pointers.size === 2) {
      const g = this.twoPointerGeometry();
      this.pinchDist = g.dist;
      this.pinchMid = g.mid;
    }
  };

  private handleMove = (e: PointerEvent): void => {
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    const [x, y] = this.local(e);
    const dx = x - p.x, dy = y - p.y;
    p.x = x;
    p.y = y;
    if (this.grab) {
      if (this.grab.id === e.pointerId) this.grab.h.move(x, y);
      return;
    }
    if (this.down && Math.hypot(x - this.down.x, y - this.down.y) > TAP_MAX_MOVE_PX) this.down.moved = true;
    const h = this.el?.clientHeight ?? 600;
    if (this.pointers.size === 1) {
      if (p.button === 2 || p.shift || e.shiftKey) {
        this.set({ target: vec3Add(this.state.target, panOffset(this.state, dx, dy, h)) });
      } else {
        this.set({ azimuth: this.state.azimuth - dx * 0.008, elevation: this.state.elevation + dy * 0.008 });
      }
    } else if (this.pointers.size === 2) {
      const g = this.twoPointerGeometry();
      const scale = this.pinchDist > 0 && g.dist > 0 ? this.pinchDist / g.dist : 1;
      const pan = panOffset(this.state, g.mid[0] - this.pinchMid[0], g.mid[1] - this.pinchMid[1], h);
      this.pinchDist = g.dist;
      this.pinchMid = g.mid;
      this.set({ distance: this.state.distance * scale, target: vec3Add(this.state.target, pan) });
    }
  };

  private handleUp = (e: PointerEvent): void => {
    this.pointers.delete(e.pointerId);
    if (this.grab?.id === e.pointerId) this.endGrab();
    const d = this.down;
    if (d && this.pointers.size === 0) {
      this.down = null;
      if (!d.moved && performance.now() - d.t < TAP_MAX_MS) this.onTap?.({ x: d.x, y: d.y });
    }
    if (this.pointers.size < 2) this.pinchDist = 0;
  };

  private handleWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const factor = Math.exp(Math.max(-1, Math.min(1, e.deltaY * 0.0015)));
    this.set({ distance: this.state.distance * factor });
  };
}
